import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { DatePicker } from '@/components/DatePicker';

import type { ColumnHeaderFilter } from '../types';

import {
  FILTER_CONTROL_INPUT_CLASS,
  FILTER_CONTROL_INPUT_WITH_ICON_CLASS,
  FILTER_CONTROL_SIZE_CLASS,
} from './control-classes';
import { isNoValueOperator, isRangeOperator } from './filter-operators';
import { useFilterRowDraft } from './useFilterRowDraft';

/**
 * Kontrolka edytora komórki wiersza filtrów dla kolumn text/number/date.
 * Redesign „Ant" (decyzja właścicielki 2026-09-15): w wierszu filtrów renderujemy
 * WYŁĄCZNIE czysty box z lupą (jak w Figmie) — bez inline'owego menu wyboru operatora.
 * Filtr działa na domyślnym operatorze kategorii (`useFilterRowDraft` → tekst „zawiera”,
 * liczba/data „równa się”); zaawansowane operatory (zakres/pomiędzy, is_null itd.)
 * pozostają dostępne przez popup na NAGŁÓWKU kolumny. Gdy filtr o operatorze zakresu
 * przyjdzie z popupu, defensywnie renderujemy parę od–do; operatory bez wartości nie
 * renderują pola.
 *
 * JEDNA sparametryzowana kontrolka zamiast trzech osobnych — identyczna logika
 * dla tekstu/liczby/daty, różni się tylko typem inputa (text / decimal / date).
 * Emisja idzie przez `useFilterRowDraft` (debounce w trybie serwerowym).
 */

export type FilterRowInputKind = 'text' | 'number' | 'date';

export interface FilterRowInputControlProps {
  columnId: string;
  columnLabel: string;
  kind: FilterRowInputKind;
  filter: ColumnHeaderFilter | undefined;
  onApply: (filter: ColumnHeaderFilter | null) => void;
  /** Tryb serwerowy — commit wartości odroczony (debounce). */
  debounced: boolean;
  testIdPrefix?: string;
}

// Wysokość/radius/tekst z jednego miejsca (`control-classes.ts`) — inaczej to pole
// rozjeżdżało się z dropdownem stojącym obok w tym samym wierszu filtrów. Kolory z
// tokenów (nie `gray-*`), więc motywy i dark mode działają bez dodatkowych wariantów.
const EDITOR_CLASS = FILTER_CONTROL_INPUT_CLASS;

interface EditorInputProps {
  kind: FilterRowInputKind;
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  /** Ikona lupy zamiast placeholdera-tekstu — tylko dla edytora pojedynczej wartości
   *  (nie dla pary „od–do" zakresu, gdzie nigdy nie było placeholdera). */
  showValueIcon?: boolean;
  testId?: string;
}

function EditorInput({
  kind,
  value,
  onChange,
  ariaLabel,
  showValueIcon,
  testId,
}: EditorInputProps) {
  if (kind === 'date') {
    // Stylowany `DatePicker` (popover + kalendarz) zamiast natywnego `<input type=date>`
    // — spójny z resztą kontrolek filtra; ta sama wartość `YYYY-MM-DD` w emisji.
    return (
      <DatePicker
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
        data-testid={testId}
        className={`min-w-0 flex-1 ${FILTER_CONTROL_SIZE_CLASS}`}
      />
    );
  }
  const common = {
    // `size={1}` — surowy `<input>` ma WEWNĘTRZNĄ szerokość ~20 znaków i przy układzie
    // automatycznym tabeli to ONA rozpychała kolumnę (kolumna z jedną liczbą puchła do
    // ~200 px, a tabela robiła się szersza od panelu i wymuszała poziomy scroll).
    // `min-w-0 flex-1`/`w-full` z klas pozwala rosnąć do szerokości komórki, więc pole
    // nie jest realnie węższe — zmienia się tylko punkt startowy wyliczeń szerokości.
    size: 1,
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    'aria-label': ariaLabel,
    'data-testid': testId,
  };
  const inputModeProp = kind === 'number' ? ({ inputMode: 'decimal' } as const) : {};
  if (!showValueIcon) {
    return <input type="text" {...inputModeProp} {...common} className={EDITOR_CLASS} />;
  }
  // Ikona zamiast tekstu placeholdera „Wartość" — mirror `SearchFilterRowControl`
  // (czyste pole „Szukaj…" bez operatora, ten sam wzorzec ikony). Input siedzi w
  // owijającym `div.relative` zamiast być bezpośrednim elementem flex — ikona
  // pozycjonowana `absolute`, mirror `Input.tsx`.
  return (
    <div className="relative min-w-0 flex-1">
      <span
        className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-text-muted"
        aria-hidden="true"
      >
        <Search className="h-3.5 w-3.5" />
      </span>
      <input
        type="text"
        {...inputModeProp}
        {...common}
        className={FILTER_CONTROL_INPUT_WITH_ICON_CLASS}
      />
    </div>
  );
}

export function FilterRowInputControl({
  columnId,
  columnLabel,
  kind,
  filter,
  onApply,
  debounced,
  testIdPrefix,
}: FilterRowInputControlProps) {
  const { t } = useTranslation();
  const { operator, value, rangeMin, rangeMax, setValue, setRangeMin, setRangeMax } =
    useFilterRowDraft({ field: columnId, category: kind, filter, onApply, debounced });

  return (
    <div
      role="group"
      aria-label={`${t('ui.configurableTable.filterRow.groupAriaLabel')}: ${columnLabel}`}
      className="flex flex-wrap items-center gap-1"
      data-testid={testIdPrefix}
    >
      {isRangeOperator(operator) ? (
        <>
          <EditorInput
            kind={kind}
            value={rangeMin}
            onChange={setRangeMin}
            ariaLabel={`${columnLabel} — ${t('ui.configurableTable.filterRow.rangeFrom')}`}
            testId={testIdPrefix ? `${testIdPrefix}-min` : undefined}
          />
          <span className="text-[10px] text-gray-400" aria-hidden="true">
            –
          </span>
          <EditorInput
            kind={kind}
            value={rangeMax}
            onChange={setRangeMax}
            ariaLabel={`${columnLabel} — ${t('ui.configurableTable.filterRow.rangeTo')}`}
            testId={testIdPrefix ? `${testIdPrefix}-max` : undefined}
          />
        </>
      ) : isNoValueOperator(operator) ? null : (
        <EditorInput
          kind={kind}
          value={value}
          onChange={setValue}
          ariaLabel={columnLabel}
          showValueIcon
          testId={testIdPrefix ? `${testIdPrefix}-value` : undefined}
        />
      )}
    </div>
  );
}
